import { useMemo } from 'react';
import {
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { initialTranslations } from '@app/constants';
import i18n from '@app/i18n';
import { getLanguageOptions, translationsToFormValues } from '@app/utils';
import { Namespace } from '@data/index';
import { Language, ListQuestionOptionView, QuestionDetailsView } from '@data/sdk/match';
import { TranslationType } from '@data/storage';
import {
  Button,
  AntdCol,
  AntdInput,
  AntdInputProps,
  AntdPopconfirm,
  AntdRow,
  AntdSegmented,
  AntdDivider,
  AntdTooltip,
  AntdTypography,
} from '@ui/components';
import { EditIcon } from '@ui/icons';
import { useQuestionOptions } from './hooks';

const { Text } = AntdTypography;
const MAX_OPTIONS = 15;

export type QuestionOptionProps = {
  defaultTranslations?: TranslationType;
  isLoading?: boolean;
  onAddOption?: () => void;
  options?: ListQuestionOptionView;
  question?: QuestionDetailsView;
};

type OptionProps = {
  creatingLoading?: boolean;
  deleteLoading?: boolean;
  disabledDeleting?: boolean;
  editLoading?: boolean;
  inputProps: AntdInputProps;
  onCancel: () => void;
  onDelete?: () => void;
  onEdit?: () => void;
  onSave: () => void;
  optionOnEdit?: boolean;
  withoutEnTranslation?: boolean;
};

const Option = ({
  inputProps,
  optionOnEdit,
  withoutEnTranslation,
  onSave,
  onCancel,
  onEdit,
  onDelete,
  deleteLoading,
  editLoading,
  creatingLoading,
  disabledDeleting,
}: OptionProps) => {
  return (
    <AntdRow gutter={16} align="bottom">
      <AntdCol xs={20}>
        <AntdInput
          className="w-full disabled:bg-primary-200 disabled:text-base-900"
          {...inputProps}
        />
      </AntdCol>
      <AntdCol xs={4}>
        {optionOnEdit ? (
          <div className="flex gap-2">
            <AntdTooltip title={withoutEnTranslation ? 'English translation is required!' : null}>
              <Button
                icon={<CheckOutlined />}
                className="text-primary-600 p-1 h-fit disabled:text-base-400"
                type="text"
                onClick={onSave}
                disabled={withoutEnTranslation}
                loading={creatingLoading || editLoading}
              />
            </AntdTooltip>
            <Button
              icon={<CloseOutlined />}
              className="text-error p-1 h-fit"
              type="text"
              onClick={onCancel}
            />
          </div>
        ) : (
          <div className="flex gap-2">
            <Button
              icon={<EditIcon />}
              className="text-primary-600 p-1 h-fit"
              type="text"
              loading={editLoading}
              onClick={onEdit}
            />
            <AntdPopconfirm
              title=""
              description={i18n.t('admin:question_page.common.delete_option_text')}
              icon={null}
              okButtonProps={{ danger: true }}
              onConfirm={onDelete}
            >
              <AntdTooltip
                title={
                  disabledDeleting
                    ? i18n.t('admin:question_page.common.at_least_2_options_required')
                    : null
                }
              >
                <Button
                  icon={<DeleteOutlined />}
                  className="text-error p-1 h-fit disabled:text-base-400"
                  type="text"
                  loading={deleteLoading}
                  disabled={disabledDeleting}
                />
              </AntdTooltip>
            </AntdPopconfirm>
          </div>
        )}
      </AntdCol>
    </AntdRow>
  );
};

export const QuestionOptions = ({
  options,
  defaultTranslations,
  isLoading,
  onAddOption,
}: QuestionOptionProps) => {
  const { t } = useTranslation([Namespace.ADMIN]);
  const {
    currentLanguage,
    error,
    newOption,
    optionToEdit,
    optionIsLoading,
    createIsLoading,
    setNewOption,
    setOptionToEdit,
    handleEditOption,
    handleCreateOption,
    handleRemoveOption,
    setCurrentLanguage,
  } = useQuestionOptions({ defaultTranslations, onAddOption });

  const optionsWithTranslations =
    useMemo(() => {
      return options?.items.map((o) => ({
        ...o,
        translations: o.translations
          ? translationsToFormValues(o.translations)
          : initialTranslations,
      }));
    }, [options]) || [];

  return (
    <div className="flex flex-col gap-1">
      <div className="p-4 rounded-default flex flex-col gap-2 border border-base-200 border-solid">
        <div className="flex gap-2 items-start mb-1">
          <AntdRow className="w-full" align="bottom">
            <AntdCol lg={12}>
              <AntdSegmented
                className="lang-segment mt-1 w-min"
                options={getLanguageOptions()}
                value={currentLanguage}
                onChange={setCurrentLanguage}
                disabled={isLoading}
              />
            </AntdCol>

            <AntdCol lg={12} className="text-end">
              <AntdTooltip
                title={
                  Number(options?.count) >= MAX_OPTIONS
                    ? t('question_page.common.max_15_options_available')
                    : null
                }
              >
                <Button
                  icon={<PlusOutlined />}
                  className="text-primary-600 bg-transparent border-primary-600 disabled:text-base-400 disabled:border-base-400"
                  onClick={() => setNewOption({ translations: initialTranslations })}
                  disabled={!!newOption || Number(options?.count) >= MAX_OPTIONS}
                >
                  {t('patient:common.add')}
                </Button>
              </AntdTooltip>
            </AntdCol>
          </AntdRow>
        </div>
        <Text className="text-base-600 text-xs">
          <InfoCircleOutlined /> {t('question_page.common.at_least_2_options_required')}
        </Text>

        <AntdDivider className="my-0" />

        <div className="flex flex-col gap-2">
          {optionsWithTranslations.map((option) => (
            <Option
              key={option.id}
              inputProps={{
                value:
                  optionToEdit?.id === option.id
                    ? optionToEdit?.translations[currentLanguage]
                    : option.translations[currentLanguage],
                disabled: optionToEdit?.id !== option.id,
                onChange: (e) =>
                  setOptionToEdit({
                    id: option.id,
                    translations: {
                      ...option.translations,
                      [currentLanguage]: e.target.value,
                    },
                  }),
                status:
                  optionToEdit?.id === option.id && !optionToEdit?.translations[Language.En].trim()
                    ? 'error'
                    : undefined,
              }}
              optionOnEdit={optionToEdit?.id === option.id}
              withoutEnTranslation={
                optionToEdit?.id === option.id && !optionToEdit?.translations[Language.En].trim()
              }
              onSave={handleEditOption}
              onCancel={() => setOptionToEdit(null)}
              onEdit={() =>
                setOptionToEdit({
                  id: option.id,
                  translations: option.translations,
                })
              }
              onDelete={() => handleRemoveOption(option.id)}
              editLoading={optionToEdit?.id === option.id && optionIsLoading}
              creatingLoading={createIsLoading}
              disabledDeleting={(options?.count || 0) <= 2}
            />
          ))}
        </div>

        {newOption && (
          <Option
            inputProps={{
              value: newOption.translations[currentLanguage],
              onChange: (e) =>
                setNewOption({
                  translations: { ...newOption.translations, [currentLanguage]: e.target.value },
                }),
              autoFocus: true,
            }}
            withoutEnTranslation={!newOption?.translations[Language.En].trim()}
            onSave={handleCreateOption}
            onCancel={() => setNewOption(null)}
            optionOnEdit={true}
            creatingLoading={createIsLoading}
          />
        )}
      </div>
      {error && (
        <Text type="danger" className="text-xs">
          {error}
        </Text>
      )}
    </div>
  );
};
